import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RewardPopup } from '../components/LeaderboardReward/RewardPopup';
import { selectCurrentUser } from '../features/auth/authSlice';
import { useGetUnseenRewardsQuery, useMarkRewardSeenMutation } from '../features/leaderboards/leaderboardsSlice';

const Ctx = createContext<{ refresh: () => void }>({ refresh: () => undefined });
export const useLeaderboardReward = () => useContext(Ctx);

/** Server-driven winner popup: polls unseen prizes every minute while signed in; dismiss marks seen on the server. */
export const LeaderboardRewardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useSelector(selectCurrentUser);
  const navigate = useNavigate();
  // userId keys the cache per account, so a previous account's prizes never render after a switch.
  const { currentData: unseen, refetch, isUninitialized } = useGetUnseenRewardsQuery({ userId: user?.id }, { skip: !user, pollingInterval: 60_000 });
  const [markSeen, { isLoading }] = useMarkRewardSeenMutation();
  // Every id dismissed this session (a failed PATCH is not re-offered until the next page load).
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible' && user && !isUninitialized) refetch(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, refetch, isUninitialized]);

  const current = (unseen ?? []).find((r) => !dismissed.has(r._id)) ?? null;
  const dismiss = useCallback(() => {
    if (!current) return;
    const id = current._id;
    setDismissed((prev) => new Set(prev).add(id));
    markSeen(id).unwrap().catch(() => undefined); // stays dismissed this session; still unseen server-side for the next load
  }, [current, markSeen]);
  const viewBoard = useCallback(() => {
    if (!current) return;
    const { board, periodType } = current;
    dismiss();
    navigate(`/app/leaderboards?board=${board}&periodType=${periodType}`);
  }, [current, dismiss, navigate]);

  return (
    <Ctx.Provider value={{ refresh: () => { if (!isUninitialized) refetch(); } }}>
      {children}
      <RewardPopup reward={user ? current : null} onViewBoard={viewBoard} onDismiss={dismiss} busy={isLoading} />
    </Ctx.Provider>
  );
};
